import BasketCard from '../../../../components/cards/BasketCard/index.vue';
import RaffleBlock from '@/components/raffle-block/index.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import navigateTo from '../../../../mixins/buy-flow';
import saleRows from '@/mixins/sale-rows';
import { ecommerceViewCartEvent, hasVisibleDiscount, PRODUCT_TYPE } from '@/helpers';
import { globalLoader } from '@/helpers/variables';
import { debounce } from 'lodash';

export default {
  name: 'buy-flow',
  components: {
    BasketCard,
    RaffleBlock
  },
  mixins: [navigateTo, saleRows],
  data() {
    return {
      PRODUCT_TYPE,
      kod: '',
      showBonusesInput: false,
      bonusesAmount: 0,
      disabledButton: false,
      width: window.innerWidth,
      syncPriceProductId: null
    };
  },

  async created() {
    globalLoader(true);
    this.resetRemovedSamples();
    await Promise.all([this._sync(), this._basketSync()]);
    globalLoader(false);

    let ecommerceData = this.basket.reduce(
      (acc, product) => {
        let { select_type, productPrices, title, brand, brandSeries, select_count } = product;
        let sum = this.rowSum(product);
        let price = select_count
          ? Number((sum / select_count).toFixed(2))
          : select_type?.price || productPrices.data[0].price;

        acc.items.push({
          item_id: select_type?.id || productPrices.data[0].id,
          item_name: title,
          item_brand: brandSeries?.title || 'Renew',
          item_category: brand?.title || '',
          item_variant: select_type?.title || productPrices.data[0].title,
          price,
          quantity: select_count
        });
        acc.value = acc.value + sum;
        return acc;
      },
      { items: [], value: 0 }
    );

    let { items, value } = ecommerceData;

    ecommerceViewCartEvent({ value: value.toFixed(2), items });
  },
  watch: {
    bonusesAmount() {
      this.setBonuses({ pay_bonus_amount: this.bonusesAmount });
      this.$refs.bonusesError.innerHTML = '';
      this.disabledButton = false;

      if (this.bonusesAmount > this.user?.bonusAmount) {
        this.$refs.bonusesError.innerHTML = `доступно ${this.user.bonusAmount + ' бонусів'}`;
        this.disabledButton = true;
      }
    }
  },
  computed: {
    ...mapGetters({
      basket: 'basket/basket',
      isUserWithCode: 'profile/isUserWithCode',
      user: 'profile/user',
      isAuthenticated: 'auth/isAuthenticated',
      samples: 'samples/samples',
      gifts: 'gifts/gifts',
      hasUnselectedGifts: 'gifts/hasUnselectedGifts',
      giftGroups: 'gifts/giftGroups',
      selectedGifts: 'gifts/selectedGifts'
    }),
    selectedGiftProducts() {
      const rows = [];
      (this.selectedGifts || []).forEach((sel) => {
        const group = this.giftGroups.find((g) => g.id === sel.giftGroupId);
        if (!group) return;
        const variant = group.products.find((v) => v.productPriceId === sel.productPriceId);
        if (!variant) return;
        const pp = variant.product.productPrices?.data?.[0] || { id: sel.productPriceId, title: '' };
        rows.push({
          id: variant.product.id,
          title: variant.product.title,
          image: variant.product.image,
          type: 'gift',
          maxCount: sel.count,
          select_count: sel.count,
          select_type: { id: pp.id, price: 0, title: pp.title || '', image: variant.product.image },
          productPrices: { data: [pp] },
          _giftKey: `${sel.giftGroupId}-${sel.productPriceId}`,
          _giftGroupId: sel.giftGroupId,
          _productPriceId: sel.productPriceId
        });
      });
      return rows;
    },
    checkBasketContainsPromotion() {
      return (
        this.basket.findIndex(
          (product) => hasVisibleDiscount(product.select_type) || product.select_type.hasIndividualDiscount
        ) !== -1
      );
    },
    isShowTotalOldSum() {
      return this.cartOldTotal > this.cartTotal;
    },
    totalOldSum() {
      let bonusesAmount = this.bonusesAmount <= this.user?.bonusAmount ? this.bonusesAmount : 0;

      return this.cartOldTotal - bonusesAmount;
    },
    totalSum() {
      let bonusesAmount = this.bonusesAmount <= this.user?.bonusAmount ? this.bonusesAmount : 0;

      return this.cartTotal - bonusesAmount;
    },
    discount() {
      if (!this.canSeeDiscount()) {
        return 0;
      }

      let sum = 0;

      this.basket.forEach((basketProduct) => {
        const clientPrice = basketProduct.client_price;

        // do not calculate for regular users
        if (!clientPrice) {
          return;
        }

        let discount = 0;

        const price = basketProduct.select_type;

        // for users with code calc actual discount, without promotion percent
        if (clientPrice.oldPrice != null && price.oldPrice != null) {
          discount = clientPrice.oldPrice - price.oldPrice;
        } else {
          discount = clientPrice.price - price.price;
        }

        sum += discount * basketProduct.select_count;
      });

      return sum;
    }
  },
  methods: {
    ...mapActions({
      getSyncBasket: 'basket/GET_SYNC_BASKET',
      updateUserAssignCode: 'profile/UPDATE_USER_DATA',
      fetchUser: 'profile/FETCH_USER_DATA',
      checkSamples: 'samples/CHECK_SAMPLES',
      checkGifts: 'gifts/CHECK_GIFTS',
      checkSales: 'sales/CHECK_SALES'
    }),
    ...mapMutations({
      changeLoginPopup: 'popups/CHANGE_LOGIN_POPUP',
      setBonuses: 'basket/SET_USER_BONUSES',
      setRemovedSampler: 'samples/SET_REMOVED_SAMPLER',
      resetRemovedSamples: 'samples/RESET_REMOVED_SAMPLES',
      modalOpen: 'app/MODAL_OPEN'
    }),

    onCheckOrderCart: debounce(async function (productId) {
      try {
        this.syncPriceProductId = productId;
        await this._basketSync();
      } catch (e) {
        console.error(e);
      } finally {
        this.syncPriceProductId = null;
      }
    }, 500),

    async _basketSync() {
      let payloadSyncProductsBasket = this.basket.map(({ id, select_count }) => ({
        id,
        count: select_count
      }));

      await this.getSyncBasket(payloadSyncProductsBasket);

      let payloadSyncAddsBasket = this.basket.reduce((acc, { select_type, select_count }) => {
        acc[`products[${select_type.id}]`] = select_count;
        return acc;
      }, {});

      await Promise.all([
        this.checkGifts(payloadSyncAddsBasket),
        this.checkSamples(payloadSyncAddsBasket),
        this.checkSales(payloadSyncAddsBasket)
      ]);
    },

    async _sync() {
      if (!this.isAuthenticated) {
        return;
      }

      await this.fetchUser();
    },

    async handleOrderClick() {
      try {
        await this._basketSync();
      } catch (e) {
        console.error(e);
      }

      if (this.hasUnselectedGifts) {
        this.modalOpen('selectGift');
        return;
      }

      this.navigateTo('buy-flow-order');
    },

    canSeeDiscount() {
      return this.user && this.isUserWithCode;
    },

    updateWidth() {
      this.width = window.innerWidth;
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateWidth);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWidth);
  }
};
